import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, History, CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminAction } from "@/lib/adminApi";
import { cn } from "@/lib/utils";
import {
  AdminPageHeader,
  AdminPanel,
  AdminTableSkeleton,
  AdminEmptyState,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

function actionTone(op?: string): "success" | "warning" | "danger" | "accent" | "neutral" {
  switch (op) {
    case "INSERT": return "success";
    case "UPDATE": return "warning";
    case "DELETE": return "danger";
    case "IMPERSONATE": return "accent";
    default: return "neutral";
  }
}

export function AdminAuditLogTab() {
  const [page, setPage] = useState(0);
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const { data: orgs } = useQuery({
    queryKey: ["admin", "organizations"],
    queryFn: () => adminAction("organizations"),
  });

  const dateFromIso = dateFrom ? new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).toISOString() : undefined;
  const dateToIso = dateTo ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59, 999).toISOString() : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit_log", page, orgFilter, actionFilter, dateFromIso, dateToIso],
    queryFn: () =>
      adminAction("audit_log", {
        page,
        org_filter: orgFilter || undefined,
        action_filter: actionFilter || undefined,
        date_from: dateFromIso,
        date_to: dateToIso,
      }),
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const clearDates = () => { setDateFrom(undefined); setDateTo(undefined); setPage(0); };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="System"
        title="Audit log"
        description="Every change made to platform data, with full before/after diffs."
        meta={`${total.toLocaleString()} records`}
      />

      <div className="flex gap-3 items-center flex-wrap">
        <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-56 h-9 text-[13px]"><SelectValue placeholder="All organisations" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organisations</SelectItem>
            {(orgs || []).map((o: any) => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-44 h-9 text-[13px]"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="INSERT">Insert</SelectItem>
            <SelectItem value="UPDATE">Update</SelectItem>
            <SelectItem value="DELETE">Delete</SelectItem>
            <SelectItem value="IMPERSONATE">Impersonate</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("h-9 text-[13px] justify-start font-normal", !dateFrom && "text-muted-foreground")}>
              <CalendarIcon className="w-3.5 h-3.5 mr-2" />
              {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setPage(0); }} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("h-9 text-[13px] justify-start font-normal", !dateTo && "text-muted-foreground")}>
              <CalendarIcon className="w-3.5 h-3.5 mr-2" />
              {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setPage(0); }} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={clearDates} className="h-9 text-[12px] text-muted-foreground">
            <X className="w-3.5 h-3.5 mr-1" /> Clear dates
          </Button>
        )}
      </div>

      {isLoading ? (
        <AdminTableSkeleton rows={8} columns={5} />
      ) : rows.length === 0 ? (
        <AdminEmptyState icon={History} title="No audit records" description="Try removing filters to see more activity." />
      ) : (
        <>
          <AdminPanel>
            <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                <tr>
                  <th className="w-10" />
                  <th className={adminTableHeadCellClass}>Timestamp</th>
                  <th className={adminTableHeadCellClass}>Action</th>
                  <th className={adminTableHeadCellClass}>Table</th>
                  <th className={adminTableHeadCellClass}>Actor</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any, i: number) => (
                  <Fragment key={r.id}>
                    <tr
                      className={"cursor-pointer " + adminTableRowClass}
                      style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
                      onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}
                    >
                      <td className="px-4 py-3.5">
                        {expandedRow === r.id
                          ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                          : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                      </td>
                      <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                        {new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className={adminTableCellClass}><AdminPill tone={actionTone(r.operation)}>{r.operation}</AdminPill></td>
                      <td className={adminTableCellClass + " font-mono text-[12px] text-foreground"}>{r.table_name}</td>
                      <td className={adminTableCellClass}>
                        {r.user_id ? (
                          <div className="min-w-0">
                            <div className="text-foreground truncate text-[13px]">{r.user_name || "Unnamed"}</div>
                            {r.user_email && <div className="text-[11px] text-muted-foreground truncate">{r.user_email}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground italic text-[13px]">System</span>
                        )}
                      </td>
                    </tr>
                    {expandedRow === r.id && (
                      <tr>
                        <td colSpan={5} style={{ background: "rgba(58,53,48,0.025)", borderTop: "1px solid rgba(58,53,48,0.06)" }}>
                          <div className="grid grid-cols-2 gap-5 px-6 py-5">
                            {r.old_values && (
                              <div>
                                <p className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground mb-2">Before</p>
                                <pre className="text-[11px] text-muted-foreground p-3 rounded-[3px] overflow-auto max-h-48 font-mono" style={{ background: "#faf7f1", border: "1px solid rgba(58,53,48,0.10)" }}>{JSON.stringify(r.old_values, null, 2)}</pre>
                              </div>
                            )}
                            {r.new_values && (
                              <div>
                                <p className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground mb-2">After</p>
                                <pre className="text-[11px] text-muted-foreground p-3 rounded-[3px] overflow-auto max-h-48 font-mono" style={{ background: "#faf7f1", border: "1px solid rgba(58,53,48,0.10)" }}>{JSON.stringify(r.new_values, null, 2)}</pre>
                              </div>
                            )}
                            {Array.isArray(r.changed_fields) && r.changed_fields.length > 0 && (
                              <div className="col-span-2">
                                <p className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground mb-2">Changed fields</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {r.changed_fields.map((f: string) => (
                                    <Badge key={f} variant="secondary" className="rounded-full px-2 py-0.5 text-[11px] font-mono">{f}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </AdminPanel>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="text-[13px] h-8">Previous</Button>
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Page {page + 1} of {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} className="text-[13px] h-8">Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
